import type { TelemetryVital } from '@/apis/telemetry';
import type { SensorReportShipment } from '@/apis/shipmentEvents';
import { getShipmentLocationParsedAddress } from '@/common/utils/utils';
import type { Device } from './devices';
import type { Extent } from './extent';
import { CHART_MEASURES } from './measures';
import { DataRange } from './range';
import { formatReadingTime, formatReportTime, zoneAbbreviation } from './time';

/** The report's name for the charts' range: Custom while zoomed to the handles' selection (PRD §5.8). */
export const reportRangeName = (range: DataRange, zoomed: boolean): string =>
  zoomed ? 'Custom' : range === DataRange.Full ? 'Full' : 'Operational';

/** One labelled field in the report's key info grid. */
export interface KeyInfoField {
  label: string;
  value: string;
}

/** What the key info grid describes: the shipment, the charts as exported, and who exported them when. */
export interface KeyInfoInput {
  shipment: SensorReportShipment;
  pickedUp: number | null;
  delivered: number | null;
  rangeName: string;
  window: Extent;
  devices: readonly Device[];
  charts: readonly TelemetryVital[];
  generatedBy: string;
  generatedAt: number;
  timeZone: string;
}

const EMPTY = '--';

const joined = (
  parts: readonly (string | null | undefined)[],
  separator: string
): string =>
  parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(separator) || EMPTY;

const place = (location: SensorReportShipment['from_location']): string =>
  joined([location?.name, getShipmentLocationParsedAddress(location)], ', ');

/**
 * The key info grid, in the PRD's order (§4.1): shipment reference, carrier and tracking number, origin,
 * destination, picked up, delivered ("in transit" until delivery), the visible devices, the range with its exact
 * bounds and zone, the charts included, and who generated the report and when.
 */
export const reportKeyInfo = ({
  shipment,
  pickedUp,
  delivered,
  rangeName,
  window,
  devices,
  charts,
  generatedBy,
  generatedAt,
  timeZone,
}: KeyInfoInput): KeyInfoField[] => [
  { label: 'Shipment', value: joined([shipment.reference_number], '') },
  {
    label: 'Carrier',
    value: joined(
      [shipment.courier?.title, shipment.courier_tracking_id],
      ' · '
    ),
  },
  { label: 'Origin', value: place(shipment.from_location) },
  { label: 'Destination', value: place(shipment.to_location) },
  {
    label: 'Picked up',
    value: pickedUp === null ? EMPTY : formatReportTime(pickedUp, timeZone),
  },
  {
    label: 'Delivered',
    value:
      delivered === null ? 'in transit' : formatReportTime(delivered, timeZone),
  },
  {
    label: 'Devices',
    value: joined(
      devices.map((device) => device.label),
      ', '
    ),
  },
  {
    label: 'Data range',
    value: `${rangeName}: ${formatReadingTime(window[0], timeZone)} to ${formatReadingTime(window[1], timeZone)} ${zoneAbbreviation(window[1], timeZone)}`,
  },
  {
    label: 'Charts',
    value: joined(
      charts.map((key) => CHART_MEASURES[key].label),
      ', '
    ),
  },
  {
    label: 'Generated',
    value: `${joined([generatedBy], '')}, ${formatReportTime(generatedAt, timeZone)}`,
  },
];

/** A block of the report as laid out, with the height it repeats (a table header) when it starts a page. */
export interface ReportBlock {
  height: number;
  carryHeight?: number;
}

/**
 * Packs the blocks onto pages in order, never splitting one: a block that doesn't fit what's left of a page starts
 * the next, adding its `carryHeight` there. A block taller than a page gets a page to itself.
 */
export const paginate = (
  blocks: readonly ReportBlock[],
  pageHeight: number
): number[][] => {
  const pages: number[][] = [];
  let page: number[] = [];
  let used = 0;
  blocks.forEach((block, index) => {
    if (page.length > 0 && used + block.height > pageHeight) {
      pages.push(page);
      page = [];
      used = block.carryHeight ?? 0;
    }
    page.push(index);
    used += block.height;
  });
  if (page.length > 0) pages.push(page);
  return pages;
};
